import React, { useMemo, useState } from 'react';
import { MapPin, Home, FileText, Loader2 } from 'lucide-react';
import { useImoveisArea } from '../../hooks/useImoveisArea';
import { usePropostasOferta } from '../../hooks/usePropostasOferta';
import ImovelDetailModal from './ImovelDetailModal';

const STATUS_COLORS = {
  draft:          'bg-slate-100 text-slate-700',
  under_review:   'bg-amber-100 text-amber-800',
  presented:      'bg-blue-100 text-blue-800',
  in_negotiation: 'bg-purple-100 text-purple-800',
  accepted:       'bg-emerald-100 text-emerald-800',
  rejected:       'bg-red-100 text-red-800',
  cancelled:      'bg-slate-200 text-slate-600',
  expired:        'bg-orange-100 text-orange-800',
};
const STATUS_LABELS = {
  draft: 'Rascunho', under_review: 'Em análise', presented: 'Apresentada',
  in_negotiation: 'Em negociação', accepted: 'Aceita', rejected: 'Recusada',
  cancelled: 'Cancelada', expired: 'Expirada',
};
const SITUACAO_LABEL = {
  in_negotiation: 'negociando',
  sold: 'vendido',
  expropriated: 'desapropriado',
  judicial: 'judicial',
  archived: 'arquivado',
};
const SITUACAO_COLOR = {
  in_negotiation: 'bg-amber-100 text-amber-700',
  sold: 'bg-emerald-100 text-emerald-700',
  expropriated: 'bg-red-100 text-red-700',
  judicial: 'bg-red-100 text-red-700',
  archived: 'bg-slate-200 text-slate-600',
};

const fmtMoneyCompact = (v) => {
  if (v == null) return null;
  const n = Number(v);
  if (!isFinite(n) || n === 0) return null;
  if (n >= 1e6) return `R$ ${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `R$ ${(n / 1e3).toFixed(0)}k`;
  return `R$ ${n.toLocaleString('pt-BR')}`;
};

/**
 * Cascata inline compacta dentro da row expandida da OfertaRow (Dashboard).
 *
 * Mais leve que AreaImoveisPropostasSection:
 *  - Tipografia menor, padding menor.
 *  - Sem criacao inline (para isso o usuario abre o OfertaDetailModal).
 *  - "ficha →" abre ImovelDetailModal direto.
 *
 * Props:
 *   ofertaId, areaId (obrigatorios)
 *   areaNome, areaM2 (do join oferta.areas)
 *   empresaId — nao usado nesta versao compacta, mantido por simetria com o
 *     componente full para chamadas futuras.
 */
const OfertaInlineCascata = ({ ofertaId, areaId, areaNome, areaM2 }) => {
  const { imoveis: areaImoveis, loading: loadingArea } = useImoveisArea(areaId);
  const { propostas, loading: loadingProp } = usePropostasOferta(ofertaId);
  const [imovelModalId, setImovelModalId] = useState(null);

  const propostasByImovel = useMemo(() => {
    const map = new Map();
    for (const p of propostas) {
      for (const pi of (p.proposta_imoveis || [])) {
        const iid = pi.imovel?.id;
        if (!iid) continue;
        if (!map.has(iid)) map.set(iid, []);
        map.get(iid).push(p);
      }
    }
    return map;
  }, [propostas]);

  if (loadingArea || loadingProp) {
    return (
      <div className="mt-1 ml-8 mr-2 px-3 py-2 text-[11px] text-slate-400 flex items-center gap-1.5">
        <Loader2 size={10} className="animate-spin" /> Carregando cascata...
      </div>
    );
  }
  if (!areaImoveis || areaImoveis.length === 0) return null;

  return (
    <>
      <div className="mt-1 ml-8 mr-2 mb-2 border-l-2 border-emerald-300 pl-3 py-2 bg-emerald-50/30 rounded-r">
        <div className="text-[11px] text-emerald-700 font-semibold mb-1.5 flex items-center gap-1 flex-wrap">
          <MapPin size={10} />
          Área: {areaNome || areaId}
          {areaM2 && <span className="font-normal">({Number(areaM2).toLocaleString('pt-BR')}m²)</span>}
          <span className="font-normal">· {areaImoveis.length} imóvel{areaImoveis.length !== 1 ? 'eis' : ''}</span>
        </div>

        {areaImoveis.map(ai => {
          const im = ai.imovel;
          if (!im) return null;
          const props = im.proprietarios || [];
          const imovelPropostas = propostasByImovel.get(im.id) || [];
          const sitLbl = SITUACAO_LABEL[im.situacao];
          const sitCls = SITUACAO_COLOR[im.situacao] || 'bg-slate-100 text-slate-600';

          return (
            <div key={im.id} className="mb-1.5 last:mb-0">
              {/* Linha 1: imovel */}
              <div className="flex items-center gap-1.5 text-[11px] flex-wrap">
                <Home size={10} className="text-slate-500 flex-shrink-0" />
                <span className="font-medium text-slate-700 truncate">{im.nome}</span>
                {im.area_terreno_m2 && (
                  <span className="text-slate-400">{Number(im.area_terreno_m2).toLocaleString('pt-BR')}m²</span>
                )}
                {sitLbl && (
                  <span className={`text-[9px] px-1 py-0.5 rounded ${sitCls}`}>{sitLbl}</span>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setImovelModalId(im.id); }}
                  className="text-[9px] text-indigo-500 hover:text-indigo-700 ml-auto flex-shrink-0"
                >
                  ficha →
                </button>
              </div>

              {/* Linha 2: proprietarios */}
              {props.length > 0 && (
                <div className="ml-4 flex flex-wrap gap-2 mt-0.5 text-[10px] text-slate-500">
                  {props.map(p => {
                    const nome = p.empresa?.empresa
                      || p.empresa?.company_name
                      || [p.contato?.first_name, p.contato?.last_name].filter(Boolean).join(' ')
                      || '—';
                    const pct = Number(p.percentual_participacao);
                    return (
                      <span key={p.id}>
                        👤 {nome}
                        {isFinite(pct) && pct < 100 ? ` (${pct}%)` : ''}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Linha 3: propostas para este imovel */}
              {imovelPropostas.length > 0 ? (
                <div className="ml-4 mt-0.5 space-y-0.5">
                  {imovelPropostas.map(p => {
                    const valorLbl = fmtMoneyCompact(p.valor_proposto);
                    return (
                      <div key={p.id} className="text-[10px] text-slate-600 flex items-center gap-1.5 flex-wrap">
                        <FileText size={9} className="text-indigo-500 flex-shrink-0" />
                        <span className="truncate max-w-[240px]" title={p.nome}>{p.nome}</span>
                        {valorLbl && (
                          <>
                            <span className="text-slate-400">·</span>
                            <span>{valorLbl}</span>
                          </>
                        )}
                        {p.status && (
                          <span className={`text-[9px] px-1 py-0.5 rounded ${STATUS_COLORS[p.status] || 'bg-slate-100 text-slate-600'}`}>
                            {STATUS_LABELS[p.status] || p.status}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="ml-4 mt-0.5 text-[10px] text-slate-400 italic">sem proposta</div>
              )}
            </div>
          );
        })}
      </div>

      {imovelModalId && (
        <ImovelDetailModal
          imovelId={imovelModalId}
          onClose={() => setImovelModalId(null)}
        />
      )}
    </>
  );
};

export default OfertaInlineCascata;
